import * as angular from "angular";
import * as _ from "lodash";
import { Campaign } from "../models/campaign.model";
import { Plan } from "../models/plan.model";
import { SubscriptionService } from "../services/subscription.service";
import { PermissionCheck } from "../shared/permission";

export class SubscriptionDataProvider {
    public plansPromise: any;
    public wasAdmin = false;
    public plans: any = [];

    private readonly $q: any;
    private readonly __env: any;
    private readonly localStorageService: any;
    private readonly permissionProvider: PermissionCheck;
    private readonly service: SubscriptionService;

    constructor(
        $q: any,
        __env: any,
        localStorageService: any,
        PermissionCheck: PermissionCheck,
        SubscriptionService: SubscriptionService
    ) {
        this.$q = $q;
        this.__env = __env;
        this.localStorageService = localStorageService;
        this.permissionProvider = PermissionCheck;
        this.service = SubscriptionService;
    }

    upgrade(id: string, data: any): Promise<Campaign> {
        const deferred = this.$q.defer();

        this.service.upgrade(_.merge({ campaignid: id }, data)).then(
            (response: object) => {
                const json = response["data"];
                if (json.campaign) {
                    let campaignData = _.merge(json.campaign, data);
                    let campaign = new Campaign(
                        this.localStorageService,
                        this.$q,
                        campaignData
                    );
                    deferred.resolve(campaign);
                } else {
                    // Pass the body through so callers can act on
                    // requires_action / client_secret for 3DS challenges.
                    deferred.reject(json);
                }
            },
            (error: any) => deferred.reject(error)
        );
        return deferred.promise;
    }

    applyPromoCode(promotionCodeId: string): Promise<any> {
        const deferred = this.$q.defer();

        this.service.applyPromo({ promotionCodeId: promotionCodeId }).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    deferred.resolve(json.data);
                } else {
                    deferred.reject(json);
                }
            },
            (error: any) => deferred.reject(error)
        );
        return deferred.promise;
    }

    card(): Promise<any> {
        const deferred = this.$q.defer();

        this.service.card().then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    deferred.resolve(json.data.card);
                } else {
                    console.warn(json);
                    deferred.reject(json);
                }
            },
            () => deferred.reject()
        );

        return deferred.promise;
    }

    upcomingInvoice(): Promise<any> {
        const deferred = this.$q.defer();

        this.service.upcoming_invoice().then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    deferred.resolve(json.data);
                } else {
                    console.warn(json);
                    deferred.reject(json);
                }
            },
            () => deferred.reject()
        );

        return deferred.promise;
    }

    checkPromoCode(code: string): Promise<any> {
        const deferred = this.$q.defer();

        this.service.promoCode({ code: code }).then(
            (response: object) => {
                const json = response["data"];
                if (json.data) {
                    deferred.resolve(json.data);
                } else {
                    deferred.reject(json.data);
                }
            },
            (error: any) => deferred.reject(error.data.data)
        );

        return deferred.promise;
    }

    getPlans(campaignId?: string, isAdmin?: boolean): Promise<any> {
        if (this.wasAdmin === isAdmin) {
            if (this.plans !== null && this.plans.length) {
                return this.$q.resolve(this.plans);
            } else if (this.plansPromise) {
                //Has been called already and has not resolved yet.
                return this.plansPromise;
            }
        }

        this.wasAdmin = isAdmin;
        const deferred = this.$q.defer();

        this.service
            .plans({ campaignId: campaignId, includeAdmin: isAdmin })
            .then((response: object) => {
                const json = response["data"];
                if (json.success) {
                    this.plans = [];
                    angular.forEach(json.data, (plan: any) => {
                        this.plans.push(new Plan(plan));
                    });
                    deferred.resolve(this.plans);
                } else {
                    deferred.reject();
                }
            });

        this.plansPromise = deferred.promise;
        return this.plansPromise;
    }

    planInfo(cpid: string): Promise<any> {
        const deferred = this.$q.defer();

        this.service.planInfo({ cpid: cpid }).then((response: object) => {
            const json = response["data"];
            if (json.success) {
                deferred.resolve(json.data);
            } else {
                deferred.reject();
            }
        });

        return deferred.promise;
    }

    paymentIntent(cpid: string): Promise<any> {
        const deferred = this.$q.defer();

        this.service.paymentIntent({ id: cpid }).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    deferred.resolve(json.data.intent);
                } else {
                    deferred.reject(json);
                }
            },
            () => deferred.reject()
        );

        return deferred.promise;
    }

    setupIntent(cpid?: string): Promise<any> {
        const deferred = this.$q.defer();

        this.service.setupIntent({ id: cpid }).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    deferred.resolve(json.data.intent);
                } else {
                    deferred.reject(json);
                }
            },
            () => deferred.reject()
        );

        return deferred.promise;
    }

    setupIntentUpgrade(cpid?: string): Promise<any> {
        const deferred = this.$q.defer();

        this.service.setupIntentUpgrade({ id: cpid }).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    deferred.resolve(json.data.intent);
                } else {
                    deferred.reject(json);
                }
            },
            () => deferred.reject()
        );

        return deferred.promise;
    }

    setupIntentSignup(): Promise<any> {
        const deferred = this.$q.defer();

        this.service.setupIntentSignup().then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    deferred.resolve(json.data.intent);
                } else {
                    deferred.reject(json);
                }
            },
            () => deferred.reject()
        );

        return deferred.promise;
    }

    paymentMethod(payment_method: any, upgrade: any): Promise<any> {
        const deferred = this.$q.defer();

        const subReq = upgrade
            ? this.service.paymentMethodUpgrade.bind(this.service)
            : this.service.paymentMethod.bind(this.service);

        subReq({ method_id: payment_method }).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    deferred.resolve(json.data);
                } else {
                    deferred.reject(json);
                }
            },
            () => deferred.reject()
        );

        return deferred.promise;
    }

    invoices(): Promise<any> {
        const deferred = this.$q.defer();

        this.service.invoices().then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    deferred.resolve(json.data);
                } else {
                    deferred.reject(json);
                }
            },
            () => deferred.reject()
        );

        return deferred.promise;
    }
}

angular
    .module("vcDataProvider")
    .service("SubscriptionDataProvider", SubscriptionDataProvider);
